import React, { FunctionComponent, useState } from "react";
import styles from "./styles.module.scss";
import { Box, Card, DialogActions, DialogContent, Toolbar } from "@mui/material";
import { FieldArray, FormikProvider, useFormik } from "formik";
import { useIsMobile } from "../../../../hooks/isMobile";
import { strings } from "../../../../localization/LocalizedStrings";
import { ToolbarButton } from "../../../toolbar/ToolbarButton";
import { FieldTitle } from "../../../Text/FieldTitle";
import { TextField } from "../../../TextField";
import { SwitchListItem } from "../../../SwitchListItem";
import { ContainedButton } from "../../../ContainedButton";
import { AlertDialog } from "../../../AlertDialog";
import { StatusCodesHelper } from "../../../../models/StatusCodesHelper";
import { toast } from "react-toastify";
import { FreeTextListItem } from "../../../FreeTextListItem";
import { ModifyFreeTextAttributeDialog } from "../../../attributes/ModifyFreeTextAttributeDialog";
import { AttributeDialog } from "../../../attributes/AttributeDialog";
import { PiperDialog } from "../../../PiperDialog";
import { IconType } from "../../../../icons";
import { AlbumIdentifierType, IAlbumIdentifier } from "../../../../hooks/albumIdentifiers";
import { useModifyAlbumIdentifier } from "../../../../hooks/albumIdentifiers/useModifyAlbumIdentifier";
import { useAddAlbumIdentifier } from "../../../../hooks/albumIdentifiers/useAddAlbumIdentifiers";
import { LabelsDesktop } from "../../../attributes/labels/LabelsDesktop";
import { SelectChipsListItem } from "../../../SelectChipsListItem";
import { ModifyGenericAttributeDialog } from "../../../attributes/ModifyGenericAttributeDialog";
import { useResettableDialog } from "../../../../hooks/resettableDialog/useResettableDialog";

export const ModifyAlbumIdentifierDialog: FunctionComponent<IModifyAlbumIdentifierDialogProps> = ({
  albumId,
  open,
  onClose,
  albumIdentifier,
  onUpdate
}) => {
  const isMobile = useIsMobile();
  const [currentCodePart, setCurrentCodePart] = useState<ICurrentCodePart>();
  const [isLoading, setIsLoading] = useState(false);
  const [isConfirmCloseAlertOpen, setIsConfirmCloseAlertOpen] = useState(false);
  const [isLabelDesktopOpen, setIsLabelDesktopOpen] = useState(false);
  const {
    state: modifyValueDialogState,
    key: modifyValueDialogKey,
    openAdd: openAddValueDialog,
    openEdit: openModifyValueDialog,
    close: closeModifyValyeDialog
  } = useResettableDialog<string>();
  const [isModifyDescriptionDialogOpen, setIsModifyDescriptionDialogOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertIsOpen, setAlertIsOpen] = useState(false);
  const { addAlbumIdentifier } = useAddAlbumIdentifier();
  const { modifyAlbumIdentifier } = useModifyAlbumIdentifier();
  const tryToClose = () => {
    if (isLoading) return;
    if (formik.dirty) {
      setIsConfirmCloseAlertOpen(true);
    } else {
      onClose();
    }
  };
  const formik = useFormik({
    initialValues: {
      codeParts: albumIdentifier?.codeParts ?? [],
      type: albumIdentifier?.type ?? "catalogue",
      label: albumIdentifier?.label,
      description: albumIdentifier?.description ?? ""
    },
    enableReinitialize: true,
    onSubmit: async ({ codeParts, label, description, type }) => {
      setIsLoading(true);
      let statusCode, responseBody;
      if (albumIdentifier) {
        const { status, body } = await modifyAlbumIdentifier({
          uuid: albumIdentifier.uuid,
          albumId,
          codeParts,
          description,
          order: albumIdentifier.order,
          type: type as AlbumIdentifierType,
          labelId: type == "catalogue" ? label?.uuid ?? null : undefined
        });
        statusCode = status;
        responseBody = body;
      } else {
        const { status, body } = await addAlbumIdentifier({
          albumId,
          codeParts,
          description,
          labelId: label?.uuid,
          type: type as AlbumIdentifierType
        });
        statusCode = status;
        responseBody = body;
      }
      if (!StatusCodesHelper.isSuccessful(statusCode)) {
        setAlertMessage(responseBody.message);
        setAlertIsOpen(true);
      } else {
        onUpdate(responseBody);
        onClose();
        formik.resetForm();
        toast.success(strings.generic_update_success);
      }
      setIsLoading(false);
    }
  });
  const handleTypeChange = (_event: React.MouseEvent<HTMLElement>, type: AlbumIdentifierType) => {
    if (type !== null) {
      formik.setFieldValue("type", type).then();
    }
  };
  return (
    <FormikProvider value={formik}>
      <PiperDialog fullWidth maxWidth={"xs"} open={open} onClose={tryToClose}>
        <Toolbar>
          <h3>{strings.album_identifier_title}</h3>
          <Box sx={{ flexGrow: 1 }} />
          <ToolbarButton
            loading={isLoading}
            onClick={tryToClose}
            tooltip={strings.close}
            iconType={IconType.Close}
          />
        </Toolbar>
        <DialogContent sx={{ paddingTop: 0, paddingBottom: 0 }}>
          <FieldTitle>{strings.album_identifier_type}</FieldTitle>
          <SwitchListItem
            className={styles.visibility}
            labels={[
              strings.album_identifier_catalogue,
              strings.album_identifier_matrix,
              strings.album_identifier_barcode,
              strings.album_identifier_other
            ]}
            values={["catalogue", "matrix", "barcode", "other"]}
            value={formik.values.type}
            disabled={isLoading}
            fullWidth={true}
            disableGutters
            handleChange={handleTypeChange}
          />
          <FieldArray
            name={"codeParts"}
            render={arrayHelpers => (
              <div>
                <FieldTitle>{strings.album_identifier_value}</FieldTitle>
                <SelectChipsListItem
                  className={styles.chips}
                  disableGutters
                  title={""}
                  chips={formik.values.codeParts.map((name, index) => ({
                    id: String(index),
                    name: name
                  }))}
                  addDisabled={formik.values.codeParts.length >= 10}
                  onAdd={openAddValueDialog}
                  onChipSelected={(index, chip) => {
                    setCurrentCodePart({
                      codePart: chip.name,
                      index: index
                    });
                    openModifyValueDialog(chip.name);
                  }}
                  onDelete={arrayHelpers.remove}
                />
                <ModifyGenericAttributeDialog
                  key={modifyValueDialogKey}
                  title={strings.album_identifier_value}
                  description={strings.album_identifier_value_description}
                  placeholder={strings.album_identifier_value}
                  missingText={strings.field_missing}
                  open={modifyValueDialogState.mode !== "closed"}
                  onClose={closeModifyValyeDialog}
                  isNumber={false}
                  canClear={false}
                  value={modifyValueDialogState.item}
                  onChange={newValue => {
                    if (modifyValueDialogState.item !== undefined) {
                      arrayHelpers.replace(currentCodePart.index, newValue);
                    } else {
                      arrayHelpers.push(newValue);
                    }
                  }}
                />
              </div>
            )}
          />
          {formik.values.type == "catalogue" && (
            <>
              <FieldTitle>{strings.label_title}</FieldTitle>
              <div className={styles.textField}>
                <TextField
                  id="label"
                  name="label"
                  size="small"
                  readOnly
                  disabled={isLoading}
                  placeholder={strings.ml_placeholder}
                  value={formik.values.label?.name ?? ""}
                  onClick={() => setIsLabelDesktopOpen(true)}
                  enterAction={() => setIsLabelDesktopOpen(true)}
                />
              </div>
            </>
          )}
          <FieldTitle>{strings.collection_description}</FieldTitle>
          <Card className={styles.descriptionCard}>
            <FreeTextListItem
              hideTitle
              disabled={isLoading}
              detail={formik.values.description ?? "--"}
              maxLines={4}
              onClick={() => setIsModifyDescriptionDialogOpen(true)}
            />
          </Card>
        </DialogContent>
        <DialogActions>
          <ContainedButton type={"submit"} onClick={formik.handleSubmit} disabled={isLoading}>
            {strings.alert_confirm}
          </ContainedButton>
        </DialogActions>
      </PiperDialog>
      <AttributeDialog open={isLabelDesktopOpen} onClose={() => setIsLabelDesktopOpen(false)}>
        <LabelsDesktop
          handleClose={() => setIsLabelDesktopOpen(false)}
          isPublic={false}
          columnWidth={isMobile ? 12 : 6}
          preselectedId={formik.values.label?.uuid}
          onLabelSelected={async label => {
            setIsLabelDesktopOpen(false);
            await formik.setFieldValue("label", label);
          }}
          onClearValue={async () => {
            setIsLabelDesktopOpen(false);
            await formik.setFieldValue("label", null);
          }}
        />
      </AttributeDialog>
      <ModifyFreeTextAttributeDialog
        title={strings.collection_description}
        description={strings.edit_description_description}
        placeholder={strings.collection_description}
        value={formik.values.description}
        open={isModifyDescriptionDialogOpen}
        onClose={() => setIsModifyDescriptionDialogOpen(false)}
        onChange={newValue => formik.setFieldValue("description", newValue)}
        onClearValue={() => formik.setFieldValue("description", null)}
      />
      <AlertDialog
        message={strings.alert_cancel_confirmation}
        open={isConfirmCloseAlertOpen}
        setOpen={setIsConfirmCloseAlertOpen}
        isConfirm={true}
        onConfirm={() => {
          onClose();
          formik.resetForm();
        }}
        confirmTitle={strings.yes}
        cancelTitle={strings.no}
      />
      <AlertDialog message={alertMessage} open={alertIsOpen} setOpen={setAlertIsOpen} />
    </FormikProvider>
  );
};

interface ICurrentCodePart {
  codePart?: string;
  index: number;
}

export interface IModifyAlbumIdentifierDialogProps {
  albumId: string;
  open: boolean;
  onClose: () => void;
  albumIdentifier?: IAlbumIdentifier;
  onUpdate: (albumIdentifier: IAlbumIdentifier) => void;
}
